// Drop-in hover overlay you place INSIDE the same `position:
// relative` container that holds the message bubble. Renders the
// Figma 2804:27483 action panel + secondary menu as absolute
// children that anchor to the bubble's right/left edge.
//
// Architecture intent:
//   • Lives inside the bubble's positioned wrapper (e.g.
//     `bubbleHoverHost` for MessageBubble, the outer container for
//     each tile component). The wrapper handles alignment via its
//     existing `justify-content`; the host just floats the panel
//     above the bubble using `bottom: 100%`.
//   • The host owns:
//       1. Hover open / close state (with 180 ms debounced close
//          so the cursor can travel the 6 px gap).
//       2. Secondary action-menu state.
//       3. Pointer-event wiring on both the bubble area AND the
//          floating panel so they share the same hover scope.
//   • Render is plain DOM `<div>`s — no react-dom portal, no
//     react-native Modal, no shared `Popover` abstraction (the
//     prior attempts depended on `require('react-dom')`, which
//     Vite refuses to resolve, silently producing zero UI).
//
// The host returns a JSX FRAGMENT:
//   [HoverDetector, HoverPanel]
// HoverDetector is a transparent overlay div absolutely positioned
// across the bubble — gives us reliable `onMouseEnter`/`Leave`
// events even when the bubble is a complex nested RN-Web tree
// where individual View hover events flicker.

import React, { useEffect, useRef, useState } from 'react';
import { View } from 'react-native';
import type { Message } from '../sync/persistence';
import { MessageInteractionPanel } from './MessageInteractionPanel';
import { WebPortal } from './composer/WebPortal';
import { injectBubbleTailStyles } from './bubbleTailCss';
import {
  MessageActionMenu,
  type MessageMenuAction,
} from './MessageActionMenu';

const IS_WEB = typeof document !== 'undefined' && typeof window !== 'undefined';

// Inject the bubble-tail `::after` stylesheet once on web (no-op native).
// Lets media/voice/etc tiles (which route through this host) carry the
// same speech tail as text bubbles when they're the last of a run.
if (IS_WEB) {
  injectBubbleTailStyles();
}

export interface MessageHoverHostProps {
  readonly message: Message;
  readonly isSelf: boolean;
  /** The bubble or tile to render inside the positioned wrapper. */
  readonly children: React.ReactNode;
  readonly onReact?: ((message: Message) => void) | undefined;
  readonly onReply?: ((message: Message) => void) | undefined;
  /** Edit this message (own text messages only). The Edit chip renders only
   *  when this is set AND `isSelf` — the host gates it to editable messages. */
  readonly onEdit?: ((message: Message) => void) | undefined;
  readonly onToggleReaction?: ((emoji: string) => void) | undefined;
  readonly onMenuAction?:
    | ((action: MessageMenuAction, message: Message) => void)
    | undefined;
  readonly isPinned?: boolean | undefined;
  /** When true, the menu's Save entry reads "Unsave" (already saved). */
  readonly isSaved?: boolean | undefined;
  /** When true, the menu's Star entry reads "Unstar" (already starred). */
  readonly isStarred?: boolean | undefined;
  /** Hide "Reply in Thread" in the action menu (inside a thread). */
  readonly hideReplyInThread?: boolean | undefined;
  /**
   * When set (web), the wrapper carries the speech-tail `::after` so the
   * tile reads as the LAST bubble of a sender run — matching text
   * bubbles. The color should be the tile's bottom-corner fill.
   */
  readonly tailColor?: string | undefined;
  /**
   * Compact / stacked mode. The row is rendered FULL-WIDTH and
   * left-aligned (Slack/Discord style) — the wrapper must NOT
   * `inline-block` shrink-wrap or align by `isSelf` (no flex-start/
   * flex-end), and the hover panel anchors to the left.
   */
  readonly stacked?: boolean | undefined;
}

const PANEL_GAP = 6;
// Snappy close: just enough for the cursor to cross the 6px gap from
// the bubble to the portaled panel (which cancels the timer on enter).
const CLOSE_DEBOUNCE_MS = 90;
// Rough heights used to decide flip direction so the panel / menu are
// never clipped by the viewport edge.
const PANEL_EST_H = 48;
const MENU_EST_H = 220;

function MessageHoverHostWeb({
    message,
    isSelf,
    children,
    onReact,
    onReply,
    onEdit,
    onToggleReaction,
    onMenuAction,
    isPinned,
    isSaved,
    isStarred,
    hideReplyInThread,
    tailColor,
    stacked,
  }: MessageHoverHostProps) {
    const [isPanelOpen, setIsPanelOpen] = useState(false);
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // Ref to the bubble wrapper + its measured viewport rect. The panel
    // is portaled to <body> and positioned against this rect so it
    // escapes the message row's stacking context (otherwise a LATER
    // media row paints over the panel of an EARLIER message).
    const wrapperRef = useRef<HTMLDivElement | null>(null);
    // The portaled panel container — used for outside-click detection so
    // the action submenu (which suppresses the hover-out close while open)
    // dismisses when the user clicks anywhere else.
    const panelRef = useRef<HTMLDivElement | null>(null);
    const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
    const measureAnchor = (): void => {
      if (wrapperRef.current !== null) {
        setAnchorRect(wrapperRef.current.getBoundingClientRect());
      }
    };

    // Outside-click / Escape dismissal for the open action menu. Without
    // this the menu lingers because hover-out is intentionally suppressed
    // while it's open (so the cursor can travel into the menu).
    useEffect(() => {
      if (!isMenuOpen) return undefined;
      const onDocPointerDown = (e: MouseEvent): void => {
        const target = e.target as Node | null;
        // Keep open ONLY when the click lands inside the actions panel
        // itself. In bubble mode the wrapper IS the small bubble, but in
        // compact the wrapper spans the FULL-WIDTH row — including the
        // wrapper there would swallow every "click out" on the row and the
        // menu would never dismiss. Anchoring dismissal to the panel makes
        // a click anywhere outside the menu (row included) close it, just
        // like bubble mode.
        if (target !== null && panelRef.current?.contains(target) === true) {
          return; // click inside the panel — keep open
        }
        setIsMenuOpen(false);
        setIsPanelOpen(false);
      };
      const onKey = (e: KeyboardEvent): void => {
        if (e.key === 'Escape') {
          setIsMenuOpen(false);
          setIsPanelOpen(false);
        }
      };
      document.addEventListener('mousedown', onDocPointerDown, true);
      document.addEventListener('keydown', onKey, true);
      return () => {
        document.removeEventListener('mousedown', onDocPointerDown, true);
        document.removeEventListener('keydown', onKey, true);
      };
    }, [isMenuOpen]);

    const hasActions =
      onReact !== undefined ||
      onReply !== undefined ||
      onToggleReaction !== undefined ||
      onMenuAction !== undefined;

    const cancelClose = (): void => {
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
        closeTimerRef.current = null;
      }
    };
    const scheduleClose = (): void => {
      cancelClose();
      closeTimerRef.current = setTimeout(() => {
        setIsPanelOpen(false);
        closeTimerRef.current = null;
      }, CLOSE_DEBOUNCE_MS);
    };

    useEffect(
      () => () => {
        cancelClose();
      },
      [],
    );

    const handleHoverIn = (): void => {
      if (!hasActions) return;
      cancelClose();
      measureAnchor();
      setIsPanelOpen(true);
    };
    const handleHoverOut = (): void => {
      if (!hasActions) return;
      if (isMenuOpen) return;
      scheduleClose();
    };

    const handleQuickReaction = (emoji: string): void => {
      onToggleReaction?.(emoji);
      setIsPanelOpen(false);
    };
    const handleReactChip = (): void => {
      onReact?.(message);
      setIsPanelOpen(false);
    };
    const handleReplyChip = (): void => {
      onReply?.(message);
      setIsPanelOpen(false);
    };
    const handleEditChip = (): void => {
      onEdit?.(message);
      setIsPanelOpen(false);
    };
    const handleMoreChip = (): void => {
      setIsMenuOpen((prev) => !prev);
    };
    const handleMenuSelect = (action: MessageMenuAction): void => {
      setIsMenuOpen(false);
      setIsPanelOpen(false);
      onMenuAction?.(action, message);
    };

    // The wrapping `<div>` is INLINE-block so it shrink-wraps to the
    // bubble's intrinsic width (lets `position: absolute; right: 0`
    // align the popover with the bubble's actual right edge, not the
    // full chat-list width). `position: relative` makes the popover
    // anchor here. `align-self` keeps the bubble on the correct side
    // when this host is a flex item.
    // Compact/stacked rows are full-width and left-aligned: a block
    // wrapper (NOT inline-block, which would shrink-wrap and collapse the
    // row's `flex:1` content column — breaking location/poll/game tiles)
    // with no `isSelf` side-alignment. Bubble mode keeps the inline-block
    // shrink-wrap + side alignment so `position:absolute;right:0` anchors
    // the hover panel to the bubble's real edge.
    const wrapperStyle: React.CSSProperties = stacked
      ? {
          position: 'relative',
          display: 'block',
          alignSelf: 'stretch',
          width: '100%',
        }
      : {
          position: 'relative',
          display: 'inline-block',
          alignSelf: isSelf ? 'flex-end' : 'flex-start',
          ...(tailColor !== undefined
            ? ({ ['--ping-tail-color' as string]: tailColor } as React.CSSProperties)
            : {}),
        };
    const wrapperClassName =
      !stacked && tailColor !== undefined
        ? `ping-bubble-tail ping-bubble-tail-${isSelf ? 'out' : 'in'}`
        : undefined;

    // Flip the panel BELOW the bubble when there isn't room above (e.g.
    // a message near the top of the viewport), so it's never clipped.
    const viewportH = typeof window !== 'undefined' ? window.innerHeight : 0;
    const placeBelow =
      anchorRect !== null && anchorRect.top < PANEL_EST_H + PANEL_GAP + 8;
    // The action submenu opens DOWN from the panel by default, but flips
    // UP when the panel sits in the lower part of the screen (so the
    // ~220px menu of a last/bottom message isn't cut off the bottom).
    const panelTopApprox =
      anchorRect === null
        ? 0
        : placeBelow
          ? anchorRect.bottom + PANEL_GAP
          : anchorRect.top - PANEL_EST_H - PANEL_GAP;
    const openMenuUp = viewportH > 0 && panelTopApprox + MENU_EST_H > viewportH;

    // Portaled panel container — fixed to the viewport at the bubble's
    // measured rect. zIndex sits in <body>'s root stacking context so
    // nothing in the message list can paint over it.
    const portalContainerStyle: React.CSSProperties = anchorRect
      ? {
          position: 'fixed',
          ...(stacked
            ? // Compact: sit above the row (or below when near the top),
              // same flip logic as bubble mode. The 90ms close debounce
              // keeps the panel alive while the cursor crosses the gap.
              placeBelow
              ? { top: anchorRect.bottom, transform: `translateY(${PANEL_GAP}px)` }
              : { top: anchorRect.top, transform: `translateY(-100%) translateY(-${PANEL_GAP}px)` }
            : placeBelow
              ? { top: anchorRect.bottom, transform: `translateY(${PANEL_GAP}px)` }
              : {
                  top: anchorRect.top,
                  transform: `translateY(-100%) translateY(-${PANEL_GAP}px)`,
                }),
          // Compact/stacked rows are full-width, so the actions popover
          // anchors to the row's RIGHT edge (top-right, inside the row /
          // hovered tint) — never the left gutter. Bubble mode keeps
          // self→right, other→left.
          ...(stacked || isSelf
            ? { right: Math.max(8, window.innerWidth - anchorRect.right) }
            : { left: Math.max(8, anchorRect.left) }),
          zIndex: 100000,
          pointerEvents: 'auto',
        }
      : {};

    const menuStyle: React.CSSProperties = {
      position: 'absolute',
      ...(openMenuUp
        ? { bottom: '100%', marginBottom: PANEL_GAP }
        : { top: '100%', marginTop: PANEL_GAP }),
      ...(stacked || isSelf ? { right: 0 } : { left: 0 }),
      zIndex: 1,
      pointerEvents: 'auto',
    };

    const wrapperHandlers = {
      onMouseEnter: handleHoverIn,
      onMouseLeave: handleHoverOut,
      // Re-open if a prior close fired while the cursor is still over the
      // row — e.g. after hovering the portaled actions panel and moving
      // back onto the row, the panel (a separate DOM subtree) had
      // scheduled a close; any movement over the row cancels it and shows
      // the popover again. Cheap: a no-op when already open.
      onMouseMove: (): void => {
        if (!hasActions) return;
        cancelClose();
        if (!isPanelOpen) handleHoverIn();
      },
    };

    return React.createElement(
      'div',
      {
        ref: wrapperRef,
        style: wrapperStyle,
        ...(wrapperClassName !== undefined ? { className: wrapperClassName } : {}),
        ...wrapperHandlers,
      },
      children,
      isPanelOpen && anchorRect !== null
        ? React.createElement(
            WebPortal,
            null,
            React.createElement(
              'div',
              {
                ref: panelRef,
                style: portalContainerStyle,
                onMouseEnter: () => {
                  cancelClose();
                  setIsPanelOpen(true);
                },
                onMouseLeave: () => {
                  if (!isMenuOpen) scheduleClose();
                },
              },
              <MessageInteractionPanel
                onSelectQuickReaction={handleQuickReaction}
                {...(onReact !== undefined ? { onReact: handleReactChip } : {})}
                {...(onReply !== undefined ? { onReply: handleReplyChip } : {})}
                {...(onEdit !== undefined && isSelf
                  ? { onEdit: handleEditChip }
                  : {})}
                {...(onMenuAction !== undefined
                  ? { onMore: handleMoreChip }
                  : {})}
              />,
              isMenuOpen && onMenuAction !== undefined
                ? React.createElement(
                    'div',
                    { style: menuStyle },
                    <MessageActionMenu
                      onSelect={handleMenuSelect}
                      isPinned={isPinned === true}
                      isSaved={isSaved === true}
                      isStarred={isStarred === true}
                      canCopy={message.body.length > 0}
                      hideReplyInThread={hideReplyInThread === true}
                    />,
                  )
                : null,
            ),
          )
        : null,
    );
}

// Native (react-native-macos) hover affordances. react-native-macos delivers
// View `onMouseEnter`/`onMouseLeave`, so we float the (pure-RN) interaction
// panel + action menu above the bubble with RN absolute layout — no DOM
// portal / `getBoundingClientRect`. The panels themselves are the same
// components the web path uses.
const NATIVE_CLOSE_DEBOUNCE_MS = 120;

function MessageHoverHostNative({
  message,
  isSelf,
  children,
  onReact,
  onReply,
  onEdit,
  onToggleReaction,
  onMenuAction,
  isPinned,
  isSaved,
  isStarred,
  hideReplyInThread,
  stacked,
}: MessageHoverHostProps) {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const hasActions =
    onReact !== undefined ||
    onReply !== undefined ||
    onToggleReaction !== undefined ||
    onMenuAction !== undefined;

  const cancelClose = (): void => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };
  const scheduleClose = (): void => {
    cancelClose();
    closeTimerRef.current = setTimeout(() => {
      setIsPanelOpen(false);
      setIsMenuOpen(false);
      closeTimerRef.current = null;
    }, NATIVE_CLOSE_DEBOUNCE_MS);
  };
  useEffect(() => () => cancelClose(), []);

  if (!hasActions) {
    return <View>{children}</View>;
  }

  const handleHoverIn = (): void => {
    cancelClose();
    setIsPanelOpen(true);
  };
  const handleHoverOut = (): void => {
    if (isMenuOpen) return;
    scheduleClose();
  };

  return (
    <View
      style={{
        position: 'relative',
        alignSelf: stacked ? 'stretch' : isSelf ? 'flex-end' : 'flex-start',
        ...(stacked ? { width: '100%' } : {}),
      }}
      {...({ onMouseEnter: handleHoverIn, onMouseLeave: handleHoverOut } as object)}
    >
      {children}
      {isPanelOpen ? (
        <View
          pointerEvents="box-none"
          style={{
            position: 'absolute',
            bottom: '100%',
            marginBottom: PANEL_GAP,
            zIndex: 100000,
            ...(stacked || isSelf ? { right: 0 } : { left: 0 }),
          }}
          {...({ onMouseEnter: cancelClose, onMouseLeave: handleHoverOut } as object)}
        >
          <MessageInteractionPanel
            onSelectQuickReaction={(emoji) => {
              onToggleReaction?.(emoji);
              setIsPanelOpen(false);
            }}
            {...(onReact !== undefined
              ? {
                  onReact: () => {
                    onReact(message);
                    setIsPanelOpen(false);
                  },
                }
              : {})}
            {...(onReply !== undefined
              ? {
                  onReply: () => {
                    onReply(message);
                    setIsPanelOpen(false);
                  },
                }
              : {})}
            {...(onEdit !== undefined && isSelf
              ? {
                  onEdit: () => {
                    onEdit(message);
                    setIsPanelOpen(false);
                  },
                }
              : {})}
            {...(onMenuAction !== undefined
              ? { onMore: () => setIsMenuOpen((prev) => !prev) }
              : {})}
          />
          {isMenuOpen && onMenuAction !== undefined ? (
            <View
              style={{
                position: 'absolute',
                top: '100%',
                marginTop: PANEL_GAP,
                ...(stacked || isSelf ? { right: 0 } : { left: 0 }),
                zIndex: 1,
              }}
            >
              <MessageActionMenu
                onSelect={(action) => {
                  setIsMenuOpen(false);
                  setIsPanelOpen(false);
                  onMenuAction(action, message);
                }}
                isPinned={isPinned === true}
                isSaved={isSaved === true}
                isStarred={isStarred === true}
                canCopy={message.body.length > 0}
                hideReplyInThread={hideReplyInThread === true}
              />
            </View>
          ) : null}
        </View>
      ) : null}
    </View>
  );
}

export const MessageHoverHost = React.memo<MessageHoverHostProps>(
  function MessageHoverHost(props) {
    return IS_WEB ? (
      <MessageHoverHostWeb {...props} />
    ) : (
      <MessageHoverHostNative {...props} />
    );
  },
);
